// Composer Studio — 표 열/행 경계 드래그 리사이즈 오버레이(엑셀/한글식).
//
// 선택된 표의 셀 세로/가로 경계(±3px)에 얇은 hit strip을 띄운다. strip 위에서 pointer 드래그하면
// 경계 왼쪽 열(colWidths)/위쪽 행(rowHeights)을 조정한다. 드래그 중에는 onColsLive/onRowsLive로
// 연속 미리보기(히스토리 미기록), pointerup에 onColsCommit/onRowsCommit으로 1개 히스토리 엔트리를
// 커밋한다(3b-2 ResizeHandles와 동일 패턴). 속성 패널 mm 입력과 같은 데이터(colWidths/rowHeights px)라
// 양쪽이 일관된다.
//
// 좌표는 엔진 DOM(data-composer-cell / data-composer-row)의 rect를 canvas 상대(화면 px)로 읽는다.
// colWidths/rowHeights는 미스케일 논리 px이므로 델타·실측을 zoom으로 나눠 논리 px로 환산한다.
//
// 간섭 가드:
//   - hit strip은 pointerEvents:'auto'로 경계 ±3px만 덮고, 셀 안쪽은 통과시켜 기존 셀 드래그 범위
//     선택/노드 드래그를 방해하지 않는다. CanvasViewport의 셀 드래그는 mousedown 대상의
//     closest('[data-composer-cell]')로 시작되는데, strip은 셀 밖 요소라 null → 시작 안 됨.
//   - pointerdown에 stopPropagation(dnd/패닝 차단) + click stopPropagation(캔버스 클릭 선택 해제 차단).
//   - 다단(multicolumn) 전개 표는 물리 열이 패턴 열과 1:1이 아니라 열 경계 비활성.
//   - 반복 전개 복제 행은 정체성 행 === 물리 행인 경계만 유효(전개 오프셋 행 비활성).
//
// 외곽(가장자리) 경계(Word/Docs식):
//   - 오른쪽 외곽 = 마지막 열 폭, 왼쪽 외곽 = 첫 열 폭(왼쪽 드래그=grow, reverse). 가로 외곽은 hug 표
//     (전 칼럼 구체값 colWidths + width 미지정/'auto')만 활성 — fill 표는 가로가 부모 결정이라 비활성.
//   - 아래 외곽 = 마지막 행 높이, 위 외곽 = 첫 행 높이(위 드래그=grow, reverse). 세로 외곽은 fill 표도
//     활성. 단 다단·반복 전개 제약을 외곽에도 동일 적용(다단이면 열 외곽도 비활성, 마지막 행이 전개
//     오프셋이면 아래 외곽 비활성).

import { useEffect, useRef } from 'react';
import type { TableNode } from '@safetyhub/shared';
import { cssEscape, rectRelativeTo, type Rect } from './canvasRect';
import {
  resizeColumnWidths,
  resizeRowHeights,
  horizontalOuterResizeEnabled,
} from './tableResize';

/** strip 히트 반경(±3px). */
const HIT = 3;

interface TdInfo {
  idRow: number;
  idCol: number;
  colSpan: number;
  rowSpan: number;
  /** 전개 후 물리 행 인덱스(data-composer-row). */
  physRow: number;
  rect: Rect;
}

/** data-composer-cell = `${tableId}::${row}::${col}` — 뒤 두 조각이 정체성 (row,col). */
function parseIdentity(raw: string): { row: number; col: number } | null {
  const parts = raw.split('::');
  if (parts.length < 3) return null;
  const row = Number(parts[parts.length - 2]);
  const col = Number(parts[parts.length - 1]);
  if (!Number.isFinite(row) || !Number.isFinite(col)) return null;
  return { row, col };
}

/** 한 표 조각(<table> 조각)의 셀 목록을 canvas 상대 rect로 수집. */
function collectTds(canvas: HTMLElement, tableEl: HTMLElement): TdInfo[] {
  const out: TdInfo[] = [];
  tableEl.querySelectorAll<HTMLTableCellElement>('td[data-composer-cell]').forEach((td) => {
    const id = parseIdentity(td.dataset.composerCell ?? '');
    if (!id) return;
    const rowRaw = td.closest('tr')?.getAttribute('data-composer-row') ?? '';
    const rowParts = rowRaw.split('::');
    const physRow = Number(rowParts[rowParts.length - 1]);
    out.push({
      idRow: id.row,
      idCol: id.col,
      colSpan: td.colSpan || 1,
      rowSpan: td.rowSpan || 1,
      physRow: Number.isFinite(physRow) ? physRow : id.row,
      rect: rectRelativeTo(canvas, td),
    });
  });
  return out;
}

interface ColEdges {
  boundaries: { leftCol: number; x: number }[];
  /** 열별 실측 폭(화면 px, colspan 1 대표 셀). */
  widthsPx: number[];
  enabled: boolean;
  /** 첫 열 왼쪽 외곽 x(대표 셀 있을 때). null=비활성. */
  outerLeftX: number | null;
  /** 마지막 열 오른쪽 외곽 x(대표 셀 있을 때). null=비활성. */
  outerRightX: number | null;
}

function computeColEdges(tds: TdInfo[], colCount: number): ColEdges {
  // 다단 감지: 같은 물리 행에 정체성 열이 중복 등장하면 물리 열≠패턴 열 → 열 리사이즈 비활성.
  const byPhysRow = new Map<number, number[]>();
  for (const td of tds) {
    const arr = byPhysRow.get(td.physRow) ?? [];
    arr.push(td.idCol);
    byPhysRow.set(td.physRow, arr);
  }
  for (const cols of byPhysRow.values()) {
    if (new Set(cols).size !== cols.length) {
      return { boundaries: [], widthsPx: [], enabled: false, outerLeftX: null, outerRightX: null };
    }
  }
  // 각 패턴 열의 대표 단일-colspan 셀(첫 등장) rect.
  const colCell = new Map<number, Rect>();
  for (const td of tds) {
    if (td.colSpan !== 1) continue;
    if (!colCell.has(td.idCol)) colCell.set(td.idCol, td.rect);
  }
  const widthsPx = Array.from({ length: colCount }, (_, c) => colCell.get(c)?.width ?? 0);
  const boundaries: { leftCol: number; x: number }[] = [];
  for (let c = 0; c < colCount - 1; c++) {
    const r = colCell.get(c);
    if (!r) continue;
    boundaries.push({ leftCol: c, x: r.left + r.width });
  }
  const first = colCell.get(0);
  const last = colCell.get(colCount - 1);
  return {
    boundaries,
    widthsPx,
    enabled: true,
    outerLeftX: first ? first.left : null,
    outerRightX: last ? last.left + last.width : null,
  };
}

interface RowEdges {
  boundaries: { topRow: number; y: number }[];
  /** 행별 실측 높이(화면 px). */
  heightsPx: number[];
  /** 첫 행 위쪽 외곽 y. null=비활성. */
  outerTopY: number | null;
  /** 마지막 행 아래쪽 외곽 y(전개 오프셋 없을 때만). null=비활성. */
  outerBottomY: number | null;
}

function computeRowEdges(tds: TdInfo[], rowCount: number): RowEdges {
  // 정체성 행별 최상단(첫) 물리 행 셀 — 반복 전개 복제 행(idRow 중복)은 첫 등장만.
  const rowCell = new Map<number, { rect: Rect; physRow: number }>();
  for (const td of [...tds].sort((a, b) => a.physRow - b.physRow)) {
    if (td.rowSpan !== 1) continue;
    if (!rowCell.has(td.idRow)) rowCell.set(td.idRow, { rect: td.rect, physRow: td.physRow });
  }
  const heightsPx = Array.from({ length: rowCount }, (_, r) => rowCell.get(r)?.rect.height ?? 0);
  const boundaries: { topRow: number; y: number }[] = [];
  for (let r = 0; r < rowCount - 1; r++) {
    const entry = rowCell.get(r);
    // 정체성 행 === 물리 행(전개 오프셋 없음)인 경계만 유효 — 복제/시프트 행 경계 비활성.
    if (!entry || entry.physRow !== r) continue;
    boundaries.push({ topRow: r, y: entry.rect.top + entry.rect.height });
  }
  // 위 외곽 = 첫 행(물리 0). 아래 외곽 = 마지막 행이되 전개 오프셋(physRow≠rowCount-1)이면 비활성.
  const firstRow = rowCell.get(0);
  const lastRow = rowCell.get(rowCount - 1);
  return {
    boundaries,
    heightsPx,
    outerTopY: firstRow && firstRow.physRow === 0 ? firstRow.rect.top : null,
    outerBottomY:
      lastRow && lastRow.physRow === rowCount - 1
        ? lastRow.rect.top + lastRow.rect.height
        : null,
  };
}

export function TableEdgeResizeOverlay({
  canvas,
  node,
  zoom = 1,
  onColsLive,
  onColsCommit,
  onRowsLive,
  onRowsCommit,
}: {
  canvas: HTMLElement | null;
  node: TableNode;
  zoom?: number;
  onColsLive: (widths: (number | string)[]) => void;
  onColsCommit: (widths: (number | string)[]) => void;
  onRowsLive: (heights: (number | string)[]) => void;
  onRowsCommit: (heights: (number | string)[]) => void;
}) {
  // 드래그 중 window 리스너 해제기 — 드래그 도중 언마운트 시 누수 방지(ResizeHandles와 동일).
  const dragCleanupRef = useRef<(() => void) | null>(null);
  useEffect(() => {
    return () => {
      dragCleanupRef.current?.();
      dragCleanupRef.current = null;
    };
  }, []);

  if (!canvas) return null;
  const tableEls = canvas.querySelectorAll<HTMLElement>(
    `[data-composer-node-id="${cssEscape(node.id)}"]`,
  );
  if (tableEls.length === 0) return null;

  const beginColDrag = (
    tableEl: HTMLElement,
    targetCol: number,
    reverse: boolean,
    e: React.PointerEvent,
  ) => {
    e.preventDefault();
    e.stopPropagation();
    const { widthsPx } = computeColEdges(collectTds(canvas, tableEl), node.colCount);
    const widthsLogical = widthsPx.map((w) => w / zoom);
    const startX = e.clientX;
    const compute = (ev: PointerEvent) =>
      resizeColumnWidths(
        node.colWidths,
        node.colCount,
        targetCol,
        (ev.clientX - startX) / zoom,
        widthsLogical,
        undefined,
        reverse,
      );
    const onMove = (ev: PointerEvent) => onColsLive(compute(ev));
    const cleanup = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      dragCleanupRef.current = null;
    };
    function onUp(ev: PointerEvent) {
      cleanup();
      onColsCommit(compute(ev));
    }
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    dragCleanupRef.current = cleanup;
  };

  const beginRowDrag = (
    tableEl: HTMLElement,
    targetRow: number,
    reverse: boolean,
    e: React.PointerEvent,
  ) => {
    e.preventDefault();
    e.stopPropagation();
    const { heightsPx } = computeRowEdges(collectTds(canvas, tableEl), node.rowCount);
    const heightsLogical = heightsPx.map((h) => h / zoom);
    const startY = e.clientY;
    const compute = (ev: PointerEvent) =>
      resizeRowHeights(
        node.rowHeights,
        node.rowCount,
        targetRow,
        (ev.clientY - startY) / zoom,
        heightsLogical,
        undefined,
        reverse,
      );
    const onMove = (ev: PointerEvent) => onRowsLive(compute(ev));
    const cleanup = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      dragCleanupRef.current = null;
    };
    function onUp(ev: PointerEvent) {
      cleanup();
      onRowsCommit(compute(ev));
    }
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    dragCleanupRef.current = cleanup;
  };

  return (
    <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 39 }}>
      {Array.from(tableEls, (tableEl, fi) => {
        const frag = rectRelativeTo(canvas, tableEl);
        const tds = collectTds(canvas, tableEl);
        const cols = computeColEdges(tds, node.colCount);
        const rows = computeRowEdges(tds, node.rowCount);
        // 가로 외곽은 hug 표만(전 칼럼 구체값 + width 미지정/'auto') + 다단 아님(cols.enabled).
        const horizOuter =
          cols.enabled &&
          horizontalOuterResizeEnabled(node.colWidths, node.colCount, node.layout?.width);
        return (
          <div key={fi}>
            {cols.enabled &&
              cols.boundaries.map((b) => (
                <div
                  key={`c${b.leftCol}`}
                  role="separator"
                  aria-orientation="vertical"
                  aria-label={`열 ${b.leftCol} 너비 조절`}
                  onClick={(ev) => ev.stopPropagation()}
                  onPointerDown={(ev) => beginColDrag(tableEl, b.leftCol, false, ev)}
                  style={{
                    position: 'absolute',
                    left: b.x - HIT,
                    top: frag.top,
                    width: HIT * 2,
                    height: frag.height,
                    cursor: 'col-resize',
                    pointerEvents: 'auto',
                    touchAction: 'none',
                  }}
                />
              ))}
            {/* 왼쪽 외곽 = 첫 열 폭(바깥으로 끌면 grow, reverse). */}
            {horizOuter && cols.outerLeftX !== null && (
              <div
                key="c-outer-left"
                role="separator"
                aria-orientation="vertical"
                aria-label="첫 열 왼쪽 외곽 너비 조절"
                onClick={(ev) => ev.stopPropagation()}
                onPointerDown={(ev) => beginColDrag(tableEl, 0, true, ev)}
                style={{
                  position: 'absolute',
                  left: cols.outerLeftX - HIT,
                  top: frag.top,
                  width: HIT * 2,
                  height: frag.height,
                  cursor: 'col-resize',
                  pointerEvents: 'auto',
                  touchAction: 'none',
                }}
              />
            )}
            {/* 오른쪽 외곽 = 마지막 열 폭(바깥으로 끌면 grow). */}
            {horizOuter && cols.outerRightX !== null && (
              <div
                key="c-outer-right"
                role="separator"
                aria-orientation="vertical"
                aria-label="마지막 열 오른쪽 외곽 너비 조절"
                onClick={(ev) => ev.stopPropagation()}
                onPointerDown={(ev) => beginColDrag(tableEl, node.colCount - 1, false, ev)}
                style={{
                  position: 'absolute',
                  left: cols.outerRightX - HIT,
                  top: frag.top,
                  width: HIT * 2,
                  height: frag.height,
                  cursor: 'col-resize',
                  pointerEvents: 'auto',
                  touchAction: 'none',
                }}
              />
            )}
            {rows.boundaries.map((b) => (
              <div
                key={`r${b.topRow}`}
                role="separator"
                aria-orientation="horizontal"
                aria-label={`행 ${b.topRow} 높이 조절`}
                onClick={(ev) => ev.stopPropagation()}
                onPointerDown={(ev) => beginRowDrag(tableEl, b.topRow, false, ev)}
                style={{
                  position: 'absolute',
                  top: b.y - HIT,
                  left: frag.left,
                  height: HIT * 2,
                  width: frag.width,
                  cursor: 'row-resize',
                  pointerEvents: 'auto',
                  touchAction: 'none',
                }}
              />
            ))}
            {/* 위 외곽 = 첫 행 높이(바깥으로 끌면 grow, reverse). 세로 외곽은 fill 표도 활성. */}
            {rows.outerTopY !== null && (
              <div
                key="r-outer-top"
                role="separator"
                aria-orientation="horizontal"
                aria-label="첫 행 위쪽 외곽 높이 조절"
                onClick={(ev) => ev.stopPropagation()}
                onPointerDown={(ev) => beginRowDrag(tableEl, 0, true, ev)}
                style={{
                  position: 'absolute',
                  top: rows.outerTopY - HIT,
                  left: frag.left,
                  height: HIT * 2,
                  width: frag.width,
                  cursor: 'row-resize',
                  pointerEvents: 'auto',
                  touchAction: 'none',
                }}
              />
            )}
            {/* 아래 외곽 = 마지막 행 높이(바깥으로 끌면 grow). */}
            {rows.outerBottomY !== null && (
              <div
                key="r-outer-bottom"
                role="separator"
                aria-orientation="horizontal"
                aria-label="마지막 행 아래쪽 외곽 높이 조절"
                onClick={(ev) => ev.stopPropagation()}
                onPointerDown={(ev) => beginRowDrag(tableEl, node.rowCount - 1, false, ev)}
                style={{
                  position: 'absolute',
                  top: rows.outerBottomY - HIT,
                  left: frag.left,
                  height: HIT * 2,
                  width: frag.width,
                  cursor: 'row-resize',
                  pointerEvents: 'auto',
                  touchAction: 'none',
                }}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}
